import type { CorrectionsVerdict, SkillCredit } from '@orborix/shared-types';
import type {
  PronunciationCorrection,
  PronunciationQualificationsLite,
  PronunciationReportLite,
  VoiceMessage,
  VoiceMessageCorrection,
  VoiceMessageGameAttempt,
} from './types.js';

type Msgs = ReadonlyArray<VoiceMessage>;

/**
 * Normaliza texto para anclaje fuzzy (STT vs LLM-userText pueden diferir
 * en puntuación/casing/acentos).
 */
export function normalizeAnchorText(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/gu, '')
    .replace(/[^a-z0-9\s']/giu, '')
    .replace(/\s+/gu, ' ')
    .trim();
}

/**
 * A user bubble the live session's per-turn messages may anchor to: not one of
 * an earlier session (`previousSession`, a resume) nor a seeded `historical`
 * one — their turns belong to another session.
 */
function isLiveUser(m: VoiceMessage): boolean {
  return m.who === 'user' && m.previousSession === undefined && m.historical !== true;
}

/**
 * A reconnect landed on a NEW session row (`session.started` with another
 * `sessionId`: the server resumed into a child session instead of
 * re-attaching): the new session restarts the turn numbering, so every user
 * bubble so far moves its `turnIndex` to `previousSession` and stops being an
 * anchor for the new session's per-turn messages — a chip of the new turn 3
 * used to land on the old turn 3's bubble. A pending "analizando…" skeleton
 * is cleared: its done belonged to the session that ended.
 */
export function retireTurnAnchors(messages: Msgs, previousSessionId: string): Msgs {
  let changed = false;
  const next = messages.map((m) => {
    if (m.who !== 'user' || m.previousSession !== undefined || m.historical === true) return m;
    changed = true;
    const { turnIndex, correctionPending: _drop, ...rest } = m;
    return { ...rest, previousSession: { sessionId: previousSessionId, turnIndex: turnIndex ?? null } };
  });
  return changed ? next : messages;
}

/** Quedan sólo las quals presentes; null si todas eran undefined. */
export function pickQualifications(
  q:
    | {
        cefr?: string | undefined;
        ielts?: number | undefined;
        pte?: number | undefined;
        toeic?: number | undefined;
      }
    | undefined,
): PronunciationQualificationsLite | null {
  if (!q) return null;
  const out: PronunciationQualificationsLite = {};
  if (q.cefr != null) out.cefr = q.cefr;
  if (q.ielts != null) out.ielts = q.ielts;
  if (q.pte != null) out.pte = q.pte;
  if (q.toeic != null) out.toeic = q.toeic;
  return Object.keys(out).length > 0 ? out : null;
}

/**
 * Agrega una corrección de gramática al user bubble cuyo texto matchee
 * `userText`, y limpia su `correctionPending` (el skeleton se reemplaza por el
 * chip real). El LLM puede emitir varias correcciones por turno (textos
 * largos): se acumulan en `corrections[]` (append + dedup por `said`), igual
 * que `pronunciationCorrections`. Cascada de 4 pasadas, espejo de
 * `applyPronunciationCorrection`:
 *   1. match exacto de texto
 *   2. match normalizado (casing/puntuación/acentos)
 *   3. último user msg sin correcciones todavía
 *   4. último user msg (fallback con warn)
 *
 * Antes esta función SIEMPRE anclaba al último user msg (sin mirar el texto):
 * por eso la corrección del primer turno "se arrastraba" a todas las burbujas
 * cuando llegaba con un turn ya posterior abierto. El server ahora manda
 * `userText`; si falta (mensajes legacy) caemos al fallback histórico.
 */
export function applyCorrection(
  messages: Msgs,
  correction: VoiceMessageCorrection,
  userText: string | null,
): Msgs {
  const targetText = (userText ?? '').trim();
  const targetNorm = normalizeAnchorText(targetText);

  if (targetText.length > 0) {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (isLiveUser(messages[i]!) && messages[i]!.text.trim() === targetText) {
        return appendCorrectionAt(messages, i, correction);
      }
    }
  }
  if (targetNorm.length > 0) {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (isLiveUser(messages[i]!) && normalizeAnchorText(messages[i]!.text) === targetNorm) {
        return appendCorrectionAt(messages, i, correction);
      }
    }
  }
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const m = messages[i]!;
    if (isLiveUser(m) && (m.corrections?.length ?? 0) === 0) {
      return appendCorrectionAt(messages, i, correction);
    }
  }
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    if (isLiveUser(messages[i]!)) {
      if (targetText.length > 0) {
        console.warn('[voice] grammar correction anchored to last user msg (fallback)', {
          correctionUserText: targetText,
          fallbackBubble: messages[i]!.text,
        });
      }
      return appendCorrectionAt(messages, i, correction);
    }
  }
  return messages;
}

/**
 * Append inmutable de una corrección de gramática al bubble `idx`, limpiando
 * `correctionPending`. Dedup por `said` (la misma corrección reenviada no se
 * duplica). Espejo de `appendPcAt`.
 */
function appendCorrectionAt(messages: Msgs, idx: number, correction: VoiceMessageCorrection): Msgs {
  const next = messages.slice();
  const { correctionPending: _drop, ...anchored } = messages[idx]!;
  // The chip tells the bubble which turn it is (first one wins): its
  // `corrections.done` then matches by index (see `applyCorrectionsDone`).
  const rest =
    typeof correction.turnIndex === 'number' && anchored.turnIndex === undefined
      ? { ...anchored, turnIndex: correction.turnIndex }
      : anchored;
  const existing = rest.corrections ?? [];
  if (existing.some((c) => c.said === correction.said)) {
    next[idx] = rest;
    return next;
  }
  next[idx] = { ...rest, corrections: [...existing, correction] };
  return next;
}

/**
 * Marca como HABLADA (`spoken: true`) la corrección `said`/`fix` — la coda
 * del modo manos libres la dijo en voz alta (`correction.spoken`). Busca
 * backward el user bubble que ya contenga esa corrección (el chip siempre
 * llega antes que la coda: el side-runner emite `correction` y recién después
 * encola el say()). Si no la encuentra (interrupt/reconexión), no-op.
 */
export function markCorrectionSpoken(messages: Msgs, said: string, fix: string): Msgs {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const m = messages[i]!;
    if (m.who !== 'user' || !m.corrections?.length) continue;
    const idx = m.corrections.findIndex((c) => c.said === said && c.fix === fix);
    if (idx === -1) continue;
    if (m.corrections[idx]!.spoken) return messages;
    const nextCorrections = m.corrections.slice();
    nextCorrections[idx] = { ...nextCorrections[idx]!, spoken: true };
    const next = messages.slice();
    next[i] = { ...m, corrections: nextCorrections };
    return next;
  }
  return messages;
}

/**
 * The user bubble a per-turn message belongs to, by the anchor every per-turn
 * message carries (`turnIndex` + `userText`, see {@link applyCorrectionsDone}
 * for the reasoning). In order:
 *
 *   1. the bubble that already carries this `turnIndex`;
 *   2. a bubble whose text matches `userText` (exact, then normalized) that
 *      does not belong to ANOTHER turn, inside the window the indexed bubbles
 *      allow (turn indexes grow with the bubble order). Among several, the
 *      `prefer`red OPEN one (still waiting for this kind of message), else the
 *      newest match (a re-delivery);
 *   3. with `openFallback`, the preferred open bubble of the window.
 *
 * `-1` = no anchor: the caller drops the message loudly, never guesses.
 */
function locateTurnBubble(
  messages: Msgs,
  turnIndex: number | null,
  targetText: string,
  opts: {
    isOpen: (m: VoiceMessage) => boolean;
    prefer: 'oldest-open' | 'newest-open';
    openFallback: boolean;
  },
): number {
  const targetNorm = normalizeAnchorText(targetText);
  const known =
    turnIndex === null
      ? -1
      : messages.findIndex((m) => isLiveUser(m) && m.turnIndex === turnIndex);
  if (known !== -1) return known;
  // Window: after the last bubble of an older turn, before the first of a newer one.
  let lo = 0;
  let hi = messages.length;
  if (turnIndex !== null) {
    messages.forEach((m, i) => {
      if (!isLiveUser(m) || m.turnIndex === undefined) return;
      if (m.turnIndex < turnIndex) lo = Math.max(lo, i + 1);
      else if (m.turnIndex > turnIndex) hi = Math.min(hi, i);
    });
  }
  const free = (m: VoiceMessage): boolean =>
    isLiveUser(m) &&
    (turnIndex === null || m.turnIndex === undefined || m.turnIndex === turnIndex);
  /** The preferred open match in the window; else the newest match (a re-delivery). */
  const pick = (matches: (m: VoiceMessage) => boolean): number => {
    let newest = -1;
    let open = -1;
    for (let i = lo; i < hi; i += 1) {
      const m = messages[i]!;
      if (!free(m) || !matches(m)) continue;
      if (opts.isOpen(m)) {
        if (opts.prefer === 'oldest-open') return i;
        open = i;
      }
      newest = i;
    }
    return open !== -1 ? open : newest;
  };
  let idx = pick((m) => m.text.trim() === targetText);
  if (idx === -1 && targetNorm.length > 0) {
    idx = pick((m) => normalizeAnchorText(m.text) === targetNorm);
  }
  if (idx === -1 && opts.openFallback) idx = pick(opts.isOpen);
  return idx;
}

/**
 * Applies a translate-it `game.round` verdict (plan 47) to ITS user bubble:
 * stores the round and the meaning verdict on it (`gameAttempt`) and learns
 * the bubble's turn index — the same anchor the turn's corrections use
 * ({@link locateTurnBubble}). It usually arrives BEFORE the turn's chips (the
 * check runs before the reply), so among same-text bubbles the NEWEST one
 * still without a verdict wins, and there is no "any open bubble" fallback: a
 * pre-game bubble never gets a game verdict. No anchor → unchanged, with a
 * warn. A re-delivery (a re-checked turn) replaces the verdict.
 */
export function applyGameAttempt(
  messages: Msgs,
  round: {
    turnIndex: number | null;
    userText: string;
    attempt: VoiceMessageGameAttempt;
  },
): Msgs {
  const idx = locateTurnBubble(messages, round.turnIndex, round.userText.trim(), {
    isOpen: (m) => m.gameAttempt === undefined,
    prefer: 'newest-open',
    openFallback: false,
  });
  if (idx === -1) {
    console.warn('[voice] game.round verdict without a bubble to anchor to — verdict dropped', {
      userText: round.userText,
      verdict: round.attempt.verdict,
    });
    return messages;
  }
  const next = messages.slice();
  const target = messages[idx]!;
  next[idx] = {
    ...target,
    gameAttempt: round.attempt,
    ...(round.turnIndex !== null ? { turnIndex: round.turnIndex } : {}),
  };
  return next;
}

/**
 * Applies a turn's `corrections.done`: finds the user bubble it belongs to,
 * stores the verdict (and the turn index) on it and clears its "analizando…"
 * skeleton.
 *
 * Anchor, in order:
 *   1. the bubble that already carries this `turnIndex` (learned from a chip of
 *      the turn, or from a re-delivered done);
 *   2. a bubble whose text matches `userText` (exact, then normalized — the
 *      same text the turn's `correction` chips anchor by) that does not belong
 *      to ANOTHER turn, inside the window the indexed bubbles allow (turn
 *      indexes grow with the bubble order). With several candidates (repeated
 *      texts like "yes") the OLDEST still pending wins;
 *   3. the oldest pending bubble in that window not claimed by another turn.
 * No anchor at all → unchanged, with a warn: a verdict is never guessed onto a
 * bubble.
 *
 * Dones usually arrive in turn order, but NOT always: a superseded turn's
 * objectives judge keeps running for its credits, so its done can land after
 * the next turn's. That is why a known index wins over the text. Known limit:
 * two pending bubbles with the same text and no index yet whose dones arrive
 * out of order can still swap — the transcript does not carry the turn index.
 *
 * Older bubbles still pending are cleared too, WITHOUT a verdict. Safety net
 * only: every turn now gets its own done (a superseded one says `superseded`),
 * but a done lost on the wire must not leave a skeleton hanging forever (card
 * #318). Newer bubbles are never touched — their analysis may be in flight.
 */
export function applyCorrectionsDone(
  messages: Msgs,
  done: { turnIndex?: number | null; userText: string; verdict: CorrectionsVerdict },
): Msgs {
  const turnIndex = typeof done.turnIndex === 'number' ? done.turnIndex : null;
  const targetText = done.userText.trim();
  const idx = locateTurnBubble(messages, turnIndex, targetText, {
    isOpen: (m) => m.correctionPending === true,
    prefer: 'oldest-open',
    openFallback: true,
  });
  if (idx === -1) {
    console.warn('[voice] corrections.done without a bubble to anchor to — verdict dropped', {
      userText: targetText,
      verdict: done.verdict,
    });
    return messages;
  }
  return messages.map((m, i) => {
    if (m.who !== 'user' || i > idx) return m;
    if (i < idx && !m.correctionPending) return m;
    const { correctionPending: _drop, ...rest } = m;
    if (i !== idx) return rest;
    return {
      ...rest,
      correctionsVerdict: done.verdict,
      ...(turnIndex !== null ? { turnIndex } : {}),
    };
  });
}

/**
 * Stores a turn's `skills.credited` on ITS user bubble: the bubble that already
 * learned this `turnIndex` (from its corrections), else — among bubbles with no
 * known index — the newest with the exact `userText`, then normalised. No
 * fallback to "the last bubble" and never a bubble of another known turn: a
 * credit is never guessed onto another turn (warn + drop). Skills already on
 * the bubble are replaced by the newer credit of the same skill, never
 * duplicated.
 */
export function applySkillsCredited(
  messages: Msgs,
  credited: { turnIndex: number; userText: string; credits: ReadonlyArray<SkillCredit> },
): Msgs {
  const targetText = credited.userText.trim();
  const targetNorm = normalizeAnchorText(targetText);
  let idx = messages.findIndex((m) => isLiveUser(m) && m.turnIndex === credited.turnIndex);
  const free = (m: VoiceMessage): boolean => isLiveUser(m) && m.turnIndex === undefined;
  for (let i = messages.length - 1; i >= 0 && idx === -1; i -= 1) {
    if (free(messages[i]!) && messages[i]!.text.trim() === targetText) idx = i;
  }
  for (let i = messages.length - 1; i >= 0 && idx === -1 && targetNorm.length > 0; i -= 1) {
    if (free(messages[i]!) && normalizeAnchorText(messages[i]!.text) === targetNorm) idx = i;
  }
  if (idx === -1) {
    console.warn('[voice] skills.credited without a bubble to anchor to — credits dropped', {
      turnIndex: credited.turnIndex,
      userText: targetText,
      skills: credited.credits.map((c) => c.skillId),
    });
    return messages;
  }
  const target = messages[idx]!;
  const incoming = new Set(credited.credits.map((c) => c.skillId));
  const merged = [...(target.skillCredits ?? []).filter((c) => !incoming.has(c.skillId)), ...credited.credits];
  const next = messages.slice();
  // The bubble learns its turn index here too: the app looks an earlier nail
  // of a radar focus up by it (`nailedFocus.priorTurns`).
  next[idx] = { ...target, skillCredits: merged, turnIndex: credited.turnIndex };
  return next;
}

/**
 * Ancla un `pronunciationReport` al user bubble cuyo texto matchee
 * `userText`. Cascada de 4 pasadas: exacto → normalizado → último sin
 * report → último user msg.
 */
export function applyPronunciationReport(
  messages: Msgs,
  userText: string,
  report: PronunciationReportLite,
): Msgs {
  const targetText = userText.trim();
  const targetNorm = normalizeAnchorText(targetText);

  for (let i = messages.length - 1; i >= 0; i -= 1) {
    if (isLiveUser(messages[i]!) && messages[i]!.text.trim() === targetText) {
      const next = messages.slice();
      next[i] = { ...messages[i]!, pronunciationReport: report };
      return next;
    }
  }
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    if (isLiveUser(messages[i]!) && normalizeAnchorText(messages[i]!.text) === targetNorm) {
      const next = messages.slice();
      next[i] = { ...messages[i]!, pronunciationReport: report };
      return next;
    }
  }
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    if (isLiveUser(messages[i]!) && !messages[i]!.pronunciationReport) {
      const next = messages.slice();
      next[i] = { ...messages[i]!, pronunciationReport: report };
      return next;
    }
  }
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    if (isLiveUser(messages[i]!)) {
      const next = messages.slice();
      next[i] = { ...messages[i]!, pronunciationReport: report };
      return next;
    }
  }
  return messages;
}

/**
 * Agrega una `pronunciationCorrection` al user bubble correcto. Cascada de
 * 4 pasadas: exacto → normalizado → bubble cuyo `pronunciationReport`
 * incluya la palabra → último user msg (con warn).
 */
export function applyPronunciationCorrection(
  messages: Msgs,
  userText: string | null,
  word: string,
  pc: PronunciationCorrection,
): Msgs {
  const targetText = (userText ?? '').trim();
  const targetNorm = normalizeAnchorText(targetText);

  if (targetText.length > 0) {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (isLiveUser(messages[i]!) && messages[i]!.text.trim() === targetText) {
        return appendPcAt(messages, i, pc);
      }
    }
  }
  if (targetNorm.length > 0) {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (isLiveUser(messages[i]!) && normalizeAnchorText(messages[i]!.text) === targetNorm) {
        return appendPcAt(messages, i, pc);
      }
    }
  }
  const wordNorm = word.toLowerCase().replace(/[^a-z0-9']/giu, '');
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const u = messages[i]!;
    if (!isLiveUser(u) || !u.pronunciationReport) continue;
    const has = u.pronunciationReport.words.some(
      (w) => w.word.toLowerCase().replace(/[^a-z0-9']/giu, '') === wordNorm,
    );
    if (has) return appendPcAt(messages, i, pc);
  }
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    if (isLiveUser(messages[i]!)) {
       
      console.warn('[voice] PA correction anchored to last user msg (fallback)', {
        correctionWord: word,
        correctionUserText: targetText,
        fallbackBubble: messages[i]!.text,
      });
      return appendPcAt(messages, i, pc);
    }
  }
  return messages;
}

function appendPcAt(messages: Msgs, idx: number, pc: PronunciationCorrection): Msgs {
  const next = messages.slice();
  const prev = messages[idx]!;
  const existing = prev.pronunciationCorrections ?? [];
  next[idx] = { ...prev, pronunciationCorrections: [...existing, pc] };
  return next;
}

/**
 * Anclar una traducción al último bot msg del historial. Devuelve la lista
 * sin cambios si no hay bot msgs todavía (el caller debe haberla stashed).
 */
export function applyTranslationToLastBot(
  messages: Msgs,
  translation: { text: string; targetLang: string },
): Msgs {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    if (messages[i]!.who === 'bot') {
      const next = messages.slice();
      next[i] = { ...messages[i]!, translation };
      return next;
    }
  }
  return messages;
}

/**
 * Dedup de `tool.result`: si llega un partial → final con el mismo
 * toolCallId, reemplazamos in-place; sino append.
 */
export function appendOrReplaceToolResult(messages: Msgs, next: VoiceMessage): Msgs {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    if (messages[i]!.who === 'tool' && messages[i]!.toolCallId === next.toolCallId) {
      const out = messages.slice();
      out[i] = next;
      return out;
    }
  }
  return [...messages, next];
}
